/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, {useEffect, useState, useCallback} from 'react'
import {css} from '@emotion/core'
import {getTheme, getThemeStyles} from '../util/theme'
import {createMarkup} from '../graphql/datoCmsDataFactory'
import {getLanguageCode} from '../util/locale'
import {getUserAgent} from '../util/userAgent'
import {
    DEFAULT_CAPTCHA_TIME,
    getLocalizedCaptchaSharedText,
    getLocalizedCaptchaPageText,
    getCaptchaData,
    getCaptchaBaseDomain,
    validateCaptcha,
    requestNewCaptcha,
} from '../util/captchaService'
import {logDpl, EVENTS} from '../util/dpl'
import {getUrlParameterByName} from '../util/url'

import Layout from '../components/Layout'

const CaptchaPage = ({languageCode}) => {
    const lang = languageCode || getLanguageCode(false)
    const layoutLanguage = languageCode || getLanguageCode()

    const {
        captchaErrorText,
        captchaReloadText,
        captchaInvalidText,
        captchaExpiredText,
        captchaMaxTriesText,
        submitButtonText,
        captchaEmptyText,
    } = getLocalizedCaptchaSharedText(lang)

    const {
        pageTitle,
        introText,
        supportText,
        placeholderText,
        caseSensitiveText,
        helpText,
        contactText,
    } = getLocalizedCaptchaPageText(lang, 'dynamic')

    // just use default for captcha page, no need to get fancy ;)
    const [theme, setTheme] = useState('light')
    const [themeStyles, setThemeStyles] = useState({})
    const [captchaData, setCaptchaData] = useState(null)
    const [captchaInputText, setCaptchaInputText] = useState(``)
    const [captchaTimerComplete, setCaptchaTimerComplete] = useState(false)
    const [showInvalidCaptchaAttemptMessage, setShowInvalidCaptchaAttemptMessage] = useState(false)
    const [showSystemError, setShowSystemError] = useState(false)
    const [showMaxTriesError, setShowMaxTriesError] = useState(false)
    const [showExpiredHmacTokenError, setShowExpiredHmacTokenError] = useState(false)
    const [showEmptyStringError, setShowEmptyStringError] = useState(false)

    const resetCaptcha = () => {
        setCaptchaInputText(``)
        setCaptchaTimerComplete(true)
        setCaptchaTimerComplete(false)
        setShowInvalidCaptchaAttemptMessage(false)
    }

    const fetchCaptchaData = useCallback(async (e) => {
        if (e) e.preventDefault()
        try {
            const bdsToken = getUrlParameterByName('bds_t') || ''
            const bdsEip = getUrlParameterByName('be') || ''
            if (bdsToken && bdsEip) {
                const data = await getCaptchaData(bdsEip, bdsToken)
                if (data.captcha_image === undefined) {
                    setShowSystemError(true)
                    return
                }
                setCaptchaData(data)
                resetCaptcha()
            } else {
                // force the catch block below to fire
                throw new Error('invalid captcha params')
            }
        } catch (e) {
            const {status} = e
            if (status === 401) {
                setShowExpiredHmacTokenError(true)
            } else {
                // setShowSystemError(true)
            }
        }
    }, [])

    const onReloadClick = (e) => {
        const el = e.target.closest('button')
        if (el && e.currentTarget.contains(el)) {
            const {captcha_image: captchaImageHash} = captchaData
            requestNewCaptcha(captchaInputText, captchaImageHash).then((resp) => {
                const {redirectUrl} = resp
                const updatedImageHash = getUrlParameterByName('image', redirectUrl)
                setCaptchaData({
                    ...captchaData,
                    ...{
                        captcha_image: updatedImageHash,
                    },
                })
                resetCaptcha()
            })
        }
    }

    useEffect(() => {
        const theme = getTheme()
        const themeStyles = getThemeStyles()
        setTheme(theme)
        setThemeStyles(themeStyles)
        if (!captchaData) {
            fetchCaptchaData()
            logDpl(EVENTS.StartpageCaptchaVisit, {
                cat: getUrlParameterByName('cat') || '',
                bc: getUrlParameterByName('bc') || '',
                be: getUrlParameterByName('be') || '',
                bi: getUrlParameterByName('bi') || '',
                bs: getUrlParameterByName('bds_s') || '',
                ua: getUserAgent() || '',
            })
        }
    }, [captchaData, fetchCaptchaData])

    const onCaptchaSubmit = (e) => {
        e.preventDefault()
        if (captchaInputText === '') {
            setShowEmptyStringError(true)
            return
        }
        const {captcha_image: captchaImageHash} = captchaData
        validateCaptcha(captchaInputText, captchaImageHash, 'dynamic').then((resp) => {
            const {redirectUrl, status_type} = resp
            logDpl(EVENTS.StartpageCaptchaSubmit, {
                captchaResponse: status_type,
                cat: getUrlParameterByName('cat') || '',
                bc: getUrlParameterByName('bc') || '',
                be: getUrlParameterByName('be') || '',
                bi: getUrlParameterByName('bi') || '',
                bs: getUrlParameterByName('bds_s') || '',
                ua: getUserAgent() || '',
            }).then(() => {
                let updatedImageHash
                switch (status_type) {
                    case 'attempt_success':
                        window.location.replace(redirectUrl)
                        break
                    case 'failed':
                        updatedImageHash = getUrlParameterByName('image', redirectUrl)
                        setCaptchaData({
                            ...captchaData,
                            ...{
                                captcha_image: updatedImageHash,
                            },
                        })
                        setCaptchaInputText(``)
                        setShowInvalidCaptchaAttemptMessage(true)
                        break
                    case 'empty':
                        updatedImageHash = getUrlParameterByName('image', redirectUrl)
                        setCaptchaData({
                            ...captchaData,
                            ...{
                                captcha_image: updatedImageHash,
                            },
                        })
                        setCaptchaInputText(``)
                        setShowInvalidCaptchaAttemptMessage(true)
                        break
                    case 'timeout':
                        setCaptchaTimerComplete(true)
                        break
                    case 'attempt_violation_error':
                        setShowMaxTriesError(true)
                        break
                    default:
                        setCaptchaInputText(``)
                        setShowInvalidCaptchaAttemptMessage(true)
                }
            })
        })
    }

    const onCaptchaInputChange = (e) => {
        const {value} = e.target
        setCaptchaInputText(value)
        if (showEmptyStringError && value.length > 0) {
            setShowEmptyStringError(false)
        }
    }

    const isErrorSet =
        showEmptyStringError ||
        showInvalidCaptchaAttemptMessage ||
        captchaTimerComplete ||
        showMaxTriesError ||
        showExpiredHmacTokenError

    useEffect(() => {
        if (!captchaTimerComplete) {
            const timer = setTimeout(() => setCaptchaTimerComplete(true), DEFAULT_CAPTCHA_TIME * 1000 * 60)
            return () => {
                clearTimeout(timer)
            }
        }
    }, [captchaTimerComplete])

    // Ensure placeholder is set correctly after component mounts
    useEffect(() => {
        const input = document.getElementById('captcha-input')
        if (input && placeholderText) {
            input.placeholder = placeholderText
        }
    }, [placeholderText])

    return (
        <>
            <Layout
                title="Startpage Captcha"
                bannerTitle={pageTitle}
                description={`Startpage's captcha page`}
                lang={layoutLanguage}
                canonicalPath="/"
                themeStyles={themeStyles}
                theme={theme}
                hidePrivacyDropdown
                noFooter
                headerOverrideStyles={`
                padding: 28.5px 39px;
                h1 {
                    font-size: 40px !important;
                    font-weight: 600;
                    @media (max-width: 860px) {
                        display: none;
                    }
                }
                div.startpage-logo{
                    width: 44px;
                    a svg {
                        height: 34px;
                        width: 150px;
                        position: absolute;
                        top: 18px;
                        left: 24px;
                    }
                }
                button.hamburger-button svg {
                    fill: ${themeStyles.headerLinkColor};
                }
            `}
            >
                <section
                    css={css`
                        background: ${themeStyles.lighterBackgroundColor};
                        p,
                        b,
                        li,
                        label,
                        span {
                            color: ${themeStyles.primaryTextColor};
                        }
                        padding: 7rem 1rem 8rem;
                    `}
                >
                    <div
                        css={css`
                            max-width: 800px;
                            margin: auto;
                            p {
                                margin-bottom: 2rem;
                            }
                            a {
                                color: ${themeStyles.linkColor};
                            }
                        `}
                    >
                        {showSystemError && (
                            <div
                                className="system-error-msg"
                                dangerouslySetInnerHTML={createMarkup(captchaErrorText)}
                                css={css`
                                    color: ${themeStyles.errorRed};
                                `}
                                aria-live="polite"
                            />
                        )}
                        {showExpiredHmacTokenError && (
                            <div
                                className="expired-error-msg"
                                dangerouslySetInnerHTML={createMarkup(captchaMaxTriesText)}
                                css={css`
                                    color: ${themeStyles.errorRed};
                                `}
                                aria-live="polite"
                            />
                        )}
                        {showMaxTriesError && (
                            <div
                                className="max-tries-error-msg"
                                dangerouslySetInnerHTML={createMarkup(captchaMaxTriesText)}
                                css={css`
                                    color: ${themeStyles.errorRed};
                                `}
                                aria-live="polite"
                            />
                        )}
                    </div>
                    <div
                        css={css`
                            margin-top: 19px;
                        `}
                    >
                        {!showSystemError && !showMaxTriesError && !showExpiredHmacTokenError && (
                            <div
                                css={css`
                                    max-width: 800px;
                                    margin: auto;
                                    p.multi-line {
                                        margin-bottom: 6px;
                                    }
                                    a {
                                        color: ${themeStyles.linkColor};
                                    }
                                `}
                            >
                                <section
                                    className="hero"
                                    css={css`
                                        margin-bottom: 32px;
                                    `}
                                >
                                    <p className="multi-line">{introText}</p>
                                    <a
                                        href={`https://support.startpage.com/hc/${lang}/articles/5144367949716-Startpage-s-use-of-CAPTCHAs`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {supportText}
                                    </a>
                                </section>
                                <section className="user-input">
                                    <form
                                        onSubmit={onCaptchaSubmit}
                                        css={css`
                                            .survey-section {
                                                margin-bottom: 1rem;
                                                display: flex;
                                                flex-direction: column;
                                            }
                                            .radio-btn,
                                            .connect {
                                                margin: 0.5rem 0;
                                            }
                                            label {
                                                &:hover {
                                                    cursor: pointer;
                                                }
                                            }
                                            input {
                                                margin-right: 0.5rem;
                                            }
                                            .vpn-select {
                                                margin-left: 0.5rem;
                                            }
                                        `}
                                    >
                                        <div
                                            className="captcha-section"
                                            css={css`
                                                border-radius: 4px;
                                                padding: 24px 0;
                                                margin: 0 0 24px;
                                                border-bottom: 1px solid ${themeStyles.boxBorderColor};
                                                p {
                                                    margin: 0;
                                                }
                                            `}
                                        >
                                            <div
                                                css={css`
                                                    display: flex;
                                                    align-items: center;
                                                    margin-bottom: 1rem;
                                                    @media (max-width: 600px) {
                                                        flex-direction: column;
                                                        align-items: flex-start;
                                                        input {
                                                            margin-bottom: 2rem;
                                                        }
                                                    }
                                                    img {
                                                        margin-bottom: 1rem;
                                                    }
                                                    input {
                                                        width: 100%;
                                                        height: 62px;
                                                        font-size: 14px;
                                                        background-color: #fbfbfd;
                                                        padding: 6px 12px;
                                                        border: 1px solid
                                                            ${isErrorSet
                                                                ? themeStyles.errorRed
                                                                : themeStyles.borderColor};
                                                        border-radius: 4px;
                                                        &::placeholder {
                                                            color: ${themeStyles.quaternaryTextColor};
                                                        }
                                                    }
                                                `}
                                            >
                                                <div
                                                    css={css`
                                                        width: 323px;
                                                        margin-right: 2rem;
                                                        @media (max-width: 600px) {
                                                            margin-right: 0;
                                                        }
                                                    `}
                                                >
                                                    <div
                                                        css={css`
                                                            display: flex;
                                                            flex-direction: column;
                                                            height: 117px;
                                                            margin: 0 0 1rem;
                                                        `}
                                                    >
                                                        {captchaData && (
                                                            <img
                                                                alt="captcha"
                                                                src={`${getCaptchaBaseDomain()}/do/captcha?image=${
                                                                    captchaData.captcha_image
                                                                }&cmd=gen_image`}
                                                                css={css`
                                                                    height: 100%;
                                                                    width: 100%;
                                                                `}
                                                            />
                                                        )}
                                                        {!captchaData && (
                                                            <img
                                                                alt="loading spinner"
                                                                src="/sp/cdn/images/captcha_loading.gif"
                                                                css={css`
                                                                    height: 30px;
                                                                    width: 30px;
                                                                    margin: auto;
                                                                `}
                                                            />
                                                        )}
                                                    </div>
                                                    <input
                                                        id="captcha-input"
                                                        type="text"
                                                        onChange={onCaptchaInputChange}
                                                        value={captchaInputText}
                                                        autoComplete="off"
                                                        autoCapitalize="none"
                                                        spellCheck="false"
                                                        aria-label="captcha input"
                                                        aria-haspopup="false"
                                                        disabled={captchaTimerComplete}
                                                        placeholder={placeholderText}
                                                        data-placeholder={placeholderText}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                css={css`
                                                    button {
                                                        color: ${themeStyles.linkColor};
                                                        text-decoration: underline;
                                                        background: transparent;
                                                        border: none;
                                                        padding: 0;
                                                        font-size: 14px;
                                                        &:hover {
                                                            cursor: pointer;
                                                        }
                                                    }
                                                    p {
                                                        font-size: 14px;
                                                    }
                                                `}
                                            >
                                                {showEmptyStringError && !captchaTimerComplete && (
                                                    <div
                                                        className="system-error-msg"
                                                        dangerouslySetInnerHTML={createMarkup(captchaEmptyText)}
                                                        css={css`
                                                            color: ${themeStyles.errorRed};
                                                        `}
                                                        aria-live="polite"
                                                    />
                                                )}
                                                {showInvalidCaptchaAttemptMessage && !captchaTimerComplete && (
                                                    <div
                                                        className="error-msg"
                                                        onClick={onReloadClick}
                                                        dangerouslySetInnerHTML={createMarkup(captchaInvalidText)}
                                                        css={css`
                                                            color: ${themeStyles.errorRed};
                                                        `}
                                                        aria-live="polite"
                                                    />
                                                )}
                                                {!isErrorSet && (
                                                    <div
                                                        onClick={onReloadClick}
                                                        css={css`
                                                            color: ${themeStyles.primaryTextColor};
                                                        `}
                                                    >
                                                        <p className="multi-line">{caseSensitiveText}</p>
                                                        <p>
                                                            {captchaReloadText}{' '}
                                                            <button type="button">Get new image</button>
                                                        </p>
                                                    </div>
                                                )}
                                                {captchaTimerComplete && (
                                                    <div
                                                        className="error-msg"
                                                        onClick={onReloadClick}
                                                        dangerouslySetInnerHTML={createMarkup(captchaExpiredText)}
                                                        css={css`
                                                            color: ${themeStyles.errorRed};
                                                        `}
                                                        aria-live="polite"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <p
                                            css={css`
                                                margin-bottom: 42px;
                                            `}
                                        >
                                            {helpText}{' '}
                                            <a
                                                href={`https://support.startpage.com/hc/${lang}/requests/new`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {contactText}
                                            </a>
                                        </p>
                                        <button
                                            type="submit"
                                            css={css`
                                                padding: 7px 14px;
                                                border: 0;
                                                border-radius: 3px 3px 0 0;
                                                background-color: ${themeStyles.submitBackgroundColor};
                                                font-size: 14px;
                                                font-weight: 500;
                                                line-height: 18px;
                                                color: ${themeStyles.submitColor};
                                                :active {
                                                    background-color: #6677fb;
                                                    color: #fff;
                                                }
                                                :focus {
                                                    background-color: #6677fb;
                                                    color: #fff;
                                                }
                                                :hover {
                                                    background-color: #6677fb;
                                                    color: #fff;
                                                }
                                            `}
                                        >
                                            {submitButtonText}
                                        </button>
                                    </form>
                                </section>
                            </div>
                        )}
                    </div>
                </section>
            </Layout>
        </>
    )
}

export default CaptchaPage
